import React from 'react';
import { cn } from '../../lib/utils';
import { Spinner } from './Spinner';

/**
 * Button — botão primário da interface.
 *
 * @typedef {Object} ButtonProps
 * @property {string|React.ElementType} [as='button'] Elemento renderizado (button | a | Link).
 * @property {'primary'|'accent'|'whatsapp'|'outline'|'ghost'|'danger'} [variant='primary'] Variante de cor.
 * @property {'sm'|'md'|'lg'} [size='md'] Tamanho.
 * @property {boolean} [loading=false] Exibe spinner e desabilita.
 * @property {boolean} [disabled=false] Desabilita o botão.
 * @property {string} [loadingText] Texto exibido durante o loading.
 * @property {string} [className] Classes adicionais.
 * @property {React.ReactNode} children Conteúdo.
 */

const variants = {
  primary:
    'bg-primary text-white shadow-soft hover:bg-primary-dark hover:-translate-y-0.5 active:translate-y-0',
  accent:
    'bg-accent text-ink shadow-soft hover:bg-accent-dark hover:text-white hover:-translate-y-0.5 active:translate-y-0',
  whatsapp:
    'bg-[#25D366] text-white shadow-soft hover:bg-[#1ebe57] hover:-translate-y-0.5 active:translate-y-0',
  outline:
    'bg-white text-ink border border-slate-200 hover:border-primary hover:text-primary hover:-translate-y-0.5',
  ghost: 'bg-transparent text-ink-soft hover:bg-slate-100',
  danger:
    'bg-danger text-white shadow-soft hover:bg-danger-dark hover:-translate-y-0.5 active:translate-y-0',
};

const sizes = {
  sm: 'h-9 px-3 text-sm gap-1.5',
  md: 'h-11 px-4 text-sm gap-2',
  lg: 'h-12 px-5 text-base gap-2',
};

export default function Button({
  as: As = 'button',
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  loadingText,
  className,
  children,
  ...props
}) {
  const isDisabled = disabled || loading;

  return (
    <As
      className={cn(
        'inline-flex items-center justify-center rounded-full font-semibold font-heading',
        'transition-transform duration-200 ease-out select-none',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
        'disabled:opacity-50 disabled:pointer-events-none',
        variants[variant],
        sizes[size],
        className
      )}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading && <Spinner size={size === 'sm' ? 'sm' : 'md'} />}
      {loading && loadingText ? loadingText : children}
    </As>
  );
}
